import { ColumnDef } from '@tanstack/react-table';
import { Check, ChevronRight, Copy } from 'lucide-react';
import { useState } from 'react';

import { TableColumnHeader } from '@/components/orderList/TableColumnHeader';

import AccreditationReviewCell from './AccreditationReviewCell';
import NoLimitButton from './NoLimitButton';
import Reset2FADialog from './Reset2FADialog';

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };

  return (
    <button
      type="button"
      onClick={handleCopy}
      className="ml-1 shrink-0 text-muted-foreground hover:text-foreground transition-colors"
      title="Copy address"
    >
      {copied ? (
        <Check className="h-3 w-3 text-emerald-500" />
      ) : (
        <Copy className="h-3 w-3" />
      )}
    </button>
  );
}

export type TCustomerWallet = {
  id: string;
  chain: string;
  walletAddress: string;
  isActive: boolean;
};

export type TCustomerInvestment = {
  id: string;
  totalAmount: number;
  status: string;
  isCrypto: boolean;
  projectedNumberOfTokens?: number;
  tokensAllocated?: number;
  createdAt: string;
  updatedAt?: string;
  completedAt?: string;
  asset?: {
    assetId: string;
    name: string;
    type: string;
    imageUrl?: string;
  };
};

export type TCustomer = {
  id: string;
  email: string;
  name: string;
  ironcustomerid: string;
  status?: string;
  isNoLimit?: boolean;
  /** SENT | SIGNED | DECLINED | VOIDED, or null when no letter was sent. */
  accreditationLetterStatus?: string | null;
  accreditationAceStatus?: string | null;
  /** null (not reviewed) | APPROVED | DENIED */
  accreditationReviewStatus?: string | null;
  wallets: TCustomerWallet[];
  createdat: string;
  updatedat: string;
  investments?: TCustomerInvestment[];
};

export const CustomersTableColumns = (
  onOpenCustomer: (customer: TCustomer) => void,
  refetch: () => void,
): ColumnDef<TCustomer>[] => [
  {
    id: 'actions',
    header: () => <span className="text-xs font-medium">Actions</span>,
    cell: ({ row }) => (
      <div className="flex items-center gap-1">
        <Reset2FADialog
          customerId={row.original.id}
          customerName={row.original.name || row.original.email}
        />
        {!row.original.isNoLimit && (
          <NoLimitButton
            customerId={row.original.id}
            customerName={row.original.name || row.original.email}
            refetch={refetch}
          />
        )}
      </div>
    ),
    enableSorting: false,
    enableHiding: false,
  },
  {
    accessorKey: 'id',
    header: ({ column }) => <TableColumnHeader column={column} title="ID" />,
    cell: ({ row }) => (
      <div className="w-[120px] truncate text-xs font-mono text-muted-foreground">
        {row.getValue('id')}
      </div>
    ),
    enableSorting: false,
    enableHiding: false,
  },
  {
    accessorKey: 'name',
    header: ({ column }) => (
      <TableColumnHeader column={column} title="Customer" />
    ),
    cell: ({ row }) => {
      const name = row.getValue('name') as string;
      const email = row.original.email;
      const ironId = row.original.ironcustomerid;
      const handleClick = () => onOpenCustomer(row.original);

      return (
        <button
          type="button"
          onClick={handleClick}
          className="flex flex-col items-start text-left group"
        >
          <span className="font-medium group-hover:text-primary group-hover:underline">
            {name}
          </span>
          <span className="text-xs text-muted-foreground">{email}</span>
          {ironId && (
            <span className="text-[11px] text-muted-foreground/80">
              Iron ID: {ironId}
            </span>
          )}
        </button>
      );
    },
  },
  {
    accessorKey: 'wallets',
    header: ({ column }) => (
      <TableColumnHeader column={column} title="Wallets" />
    ),
    cell: ({ row }) => {
      const wallets = row.original.wallets ?? [];
      if (!wallets.length) {
        return (
          <span className="text-xs text-muted-foreground">No wallets</span>
        );
      }

      return (
        <div className="flex flex-col gap-1 max-w-[260px]">
          {wallets.map((wallet) => (
            <div
              key={wallet.id}
              className="rounded-md border bg-slate-50 px-2 py-1 flex flex-col"
            >
              <span className="text-[11px] font-medium">
                {wallet.chain}{' '}
                <span
                  className={`ml-1 inline-flex items-center rounded-full px-1.5 text-[10px] font-semibold ${
                    wallet.isActive
                      ? 'bg-emerald-100 text-emerald-700'
                      : 'bg-slate-100 text-slate-600'
                  }`}
                >
                  {wallet.isActive ? 'ACTIVE' : 'INACTIVE'}
                </span>
              </span>
              <div className="flex items-center gap-1">
                <span className="text-[11px] font-mono text-muted-foreground break-all">
                  {wallet.walletAddress}
                </span>
                <CopyButton text={wallet.walletAddress} />
              </div>
            </div>
          ))}
        </div>
      );
    },
  },
  {
    accessorKey: 'status',
    header: ({ column }) => (
      <TableColumnHeader column={column} title="Status" />
    ),
    cell: ({ row }) => {
      const status = row.getValue('status') as string | undefined;
      if (!status) {
        return <span className="text-xs text-muted-foreground">-</span>;
      }

      const displayStatus = status.replace(/_/g, ' ');

      const colorMap: Record<string, string> = {
        Login: 'bg-slate-50 text-slate-700 border-slate-200',
        TwoFA_Completed: 'bg-violet-50 text-violet-700 border-violet-200',
        KYC_Init: 'bg-amber-50 text-amber-700 border-amber-200',
        KYC_Completed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
        KYC_Failed: 'bg-rose-50 text-rose-700 border-rose-200',
        Wallet_Created: 'bg-blue-50 text-blue-700 border-blue-200',
        IRON_IdentificationRequired:
          'bg-orange-50 text-orange-700 border-orange-200',
        IRON_SigningsRequired: 'bg-yellow-50 text-yellow-700 border-yellow-200',
        IRON_Active: 'bg-teal-50 text-teal-700 border-teal-200',
      };

      return (
        <span
          className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium whitespace-nowrap ${
            colorMap[status] || 'bg-slate-50 text-slate-700 border-slate-200'
          }`}
        >
          {displayStatus}
        </span>
      );
    },
  },
  {
    id: 'accreditation',
    header: () => <span className="text-xs font-medium">Accreditation</span>,
    cell: ({ row }) => <AccreditationReviewCell customer={row.original} />,
    enableSorting: false,
  },
  {
    accessorKey: 'createdat',
    header: ({ column }) => (
      <TableColumnHeader column={column} title="Created At" />
    ),
    cell: ({ row }) => {
      const value = row.getValue('createdat') as string;
      return (
        <div className="w-[160px] text-xs">
          {value ? new Date(value).toLocaleString() : '-'}
        </div>
      );
    },
  },
  {
    accessorKey: 'updatedat',
    header: ({ column }) => (
      <TableColumnHeader column={column} title="Updated At" />
    ),
    cell: ({ row }) => {
      const value = row.getValue('updatedat') as string;
      return (
        <div className="w-[160px] text-xs">
          {value ? new Date(value).toLocaleString() : '-'}
        </div>
      );
    },
  },
  {
    // Opens the customer page: details, investments, and the accreditation review
    // (Approve/Deny and the DocuSign links).
    id: 'open',
    header: () => <span className="sr-only">Open</span>,
    cell: ({ row }) => (
      <button
        type="button"
        onClick={() => onOpenCustomer(row.original)}
        className="rounded-md p-1 text-muted-foreground hover:bg-slate-100 hover:text-foreground"
        title="Open customer details"
        aria-label={`Open ${row.original.name || row.original.email}`}
      >
        <ChevronRight className="h-4 w-4" />
      </button>
    ),
    enableSorting: false,
    enableHiding: false,
  },
];
